import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { Notification } from '../models/Notification'
import { notificationSchema } from '../models/schema'
import {
  createNotification as createNotificationAPI,
  deleteNotification as deleteNotificationAPI,
  fetchNotificationById,
  fetchNotifications,
  updateNotification as updateNotificationAPI,
} from '../services/NotificationService'
import { AppDispatch, RootState } from './store'

const initialState: SBState<Notification> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'notification',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getNotificationsSuccess,
  setQuery: setNotificationsQuery,
  reset: resetNotificationState,
  resetQueryAndIds: resetNotificationQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: Notification }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectNotifications = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedNotification = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectNotificationById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectNotificationsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedNotification =
  (notification: Notification | number) => async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(getIdOrModelId<Notification>(notification))
    )

export const getNotifications = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchNotifications(params),
    [notificationSchema],
    slice.actions
  )

export const createNotification = (notification: Notification) =>
  SBAPI.CreateDispatched(
    createNotificationAPI(notification),
    notificationSchema,
    slice.actions
  )

export const getNotificationWithId = (id: number) =>
  SBAPI.FetchDispatched<Notification>(
    fetchNotificationById(id),
    notificationSchema,
    slice.actions
  )

export const updateNotification = (notification: Notification) =>
  SBAPI.UpdateDispatched<Notification>(
    updateNotificationAPI(notification),
    notification,
    slice.actions
  )

export const deleteNotification = (notification: Notification) =>
  SBAPI.DestroyDispatched<Notification>(
    deleteNotificationAPI(notification),
    notification,
    slice.actions
  )
